<template>
  <ItemPage
    title="Novo Material"
    size="sm"
    :actions="actions"
  >
    <div
      v-if="textbookCreate"
      class="white"
    >
      <v-row
        align="center"
        no-gutters
      >
        <v-col
          cols="12"
          sm="6"
        >
          <div class="font-weight-bold">
            Capa do material
          </div>
        </v-col>
        <v-col
          class="ml-auto"
          cols="auto"
        >
          <v-btn
            v-if="displayCoverButton"
            color="primary"
            text
            :disabled="loadingCover"
            @click="openUploadCoverDialog"
          >
            <v-icon left>
              mdi-cloud-upload
            </v-icon> {{ loadingCover? 'Enviando...' : 'Enviar capa' }}
          </v-btn>
          <template v-else>
            <v-icon
              left
              color="green darken-2"
            >
              mdi-cloud-check
            </v-icon> Capa enviada
          </template>
        </v-col>
      </v-row>
      <v-row
        class="mt-10 mb-10"
        align="center"
        no-gutters
      >
        <v-col
          cols="12"
          sm="6"
        >
          <div class="font-weight-bold">
            Material
          </div>
          <div class="text-body-2 text--secondary mt-1">
            Envie o material no formato (*.zip) e aguarde sua publicação.
          </div>
        </v-col>
        <v-col
          class="ml-auto"
          cols="auto"
        >
          <v-btn
            v-if="displayMaterialButton"
            color="primary"
            text
            :disabled="loadingMaterial"
            @click="openUploadMaterialDialog"
          >
            <v-icon left>
              mdi-cloud-upload
            </v-icon> {{ loadingMaterial? 'Enviando...' : 'Enviar arquivo' }}
          </v-btn>

          <template v-else>
            <v-icon
              left
              color="green darken-2"
            >
              mdi-cloud-check
            </v-icon> Arquivo enviado
          </template>
        </v-col>
      </v-row>

      <v-form
        ref="form"
        :disabled="formDisabled"
      >
        <v-row>
          <div class="col-sm-12 col-md-6 d-flex align-center">
            <v-select
              v-model="type"
              class="px-2"
              :items="types"
              item-text="name"
              item-value="id"
              label="Tipo"
            >
              <template
                v-if="type !== null"
                #append
              >
                <v-btn
                  color="primary"
                  text
                  @click="type = null"
                >
                  <v-icon>mdi-delete</v-icon>
                </v-btn>
              </template>
            </v-select>
          </div>
          <div class="col-sm-12 col-md-6 d-flex align-center">
            <v-select
              v-model="publishing_year"
              class="px-2"
              :items="years"
              item-text="name"
              item-value="value"
              label="Ano de publicação"
            >
              <template
                v-if="publishing_year !== null"
                #append
              >
                <v-btn
                  color="primary"
                  text
                  @click="publishing_year = null"
                >
                  <v-icon>mdi-delete</v-icon>
                </v-btn>
              </template>
            </v-select>
          </div>
          <div class="col-sm-12 col-md-6 d-flex align-center">
            <EducationSystemSelect
              ref="eSSelect"
              @setEducationSystem="education_system_id = $event"
            />
          </div>
          <div class="col-sm-12 col-md-6 d-flex align-center">
            <EducationStageSelect
              :education-system-id="education_system_id"
              @setEducationStage="education_stage_id = $event"
            />
          </div>
          <div
            v-if="enableGrade"
            class="col-sm-12 col-md-6 d-flex align-center"
          >
            <GradeSelect
              ref="gSelect"
              :education-stage-id="education_stage_id"
              @setGrade="grade_id = $event"
            />
          </div>
          <div
            v-if="enableVolume"
            class="col-sm-12 col-md-6 d-flex align-center"
          >
            <GradeVolumeSelect
              :grade-id="grade_id"
              @setGradeVolume="grade_volume_id = $event"
            />
          </div>
          <div
            v-if="enableSubject"
            class="col-sm-12 col-md-6 d-flex align-center"
          >
            <GradeSubjectSelect
              :grade-id="grade_id"
              @setGradeSubject="grade_subject_id = $event"
              @setGradeSubjectName="name = $event"
            />
          </div>
          <div
            v-if="enableName"
            class="col-sm-12 col-md-6 d-flex align-center"
          >
            <v-text-field
              v-model="name"
              :error-messages="formErrors.name"
              label="Nome do material"
              class="px-2 m-2"
              placeholder="Insira o titulo do material que será enviado"
              hide-details="auto"
              @input="formErrors.name = null"
            />
          </div>
          <div class="col-sm-12 col-md-6 d-flex align-center">
            <v-text-field
              v-model="order"
              class="px-2 m-2"
              :error-messages="formErrors.order"
              label="Ordem"
              type="number"
              hide-details="auto"
              @change="formErrors.order = null"
            />
          </div>
          <div class="col-12">
            <hr>
          </div>
          <div class="col-sm-12 col-md-6 d-flex align-center justify-space-between">
            Material do professor
            <v-switch
              v-model="is_teacher_material"
              class="pa-2"
              :label="is_teacher_material ? 'Sim' : 'Não'"
            />
          </div>
          <div class="col-sm-12 col-md-6 d-flex align-center">
            Publicar após salvar
            <v-switch
              v-model="should_publish_after_processing"
              class="pa-2"
              :label="should_publish_after_processing ? 'Sim' : 'Não'"
            />
          </div>
        </v-row>
      </v-form>
    </div>
    <template v-else>
      Usuário sem permissão
    </template>
  </ItemPage>
</template>

<script>
import utils from '@/api/utils.js'
import ItemPage from '@/components/pages/ItemPage.vue'
import { types as materialTypes, listTypes } from '@/enums/materialTypes'
import { listTypes as listVolumeTypes } from '@/enums/volumeTypes'
import UploadFileDialog from '@/components/settings/UploadFileDialog.vue'
import UploadImageDialog from '@/components/settings/UploadImageDialog.vue'

import EducationSystemSelect from '@/components/contents/EducationSystemSelect.vue'
import EducationStageSelect from '@/components/contents/EducationStageSelect.vue'
import GradeSelect from '@/components/contents/GradeSelect.vue'
import GradeVolumeSelect from '@/components/contents/GradeVolumeSelect.vue'
import GradeSubjectSelect from '@/components/contents/GradeSubjectSelect.vue'
import handlesServerValidation from '@/mixins/handles-server-validation.js'

export default {
  components: {
    ItemPage,
    EducationSystemSelect,
    EducationStageSelect,
    GradeSelect,
    GradeVolumeSelect,
    GradeSubjectSelect
  },

  mixins: [
    handlesServerValidation
  ],
  data () {
    return {
      textbookCreate: false,
      loading: false,
      loadingCover: false,
      loadingMaterial: false,
      name: '',
      education_system_id: null,
      education_stage_id: null,
      grade_id: null,
      grade_volume_id: null,
      grade_subject_id: null,
      publishing_year: new Date().getFullYear(),
      type: null,
      is_teacher_material: 0,
      types: [],
      materialTarget: [
        {
          value: false,
          name: 'Aluno'
        }, {
          value: true,
          name: 'Professor'
        }
      ],
      years: [
        new Date().getFullYear() + 1,
        new Date().getFullYear(),
        new Date().getFullYear() - 1,
        new Date().getFullYear() - 2,
        new Date().getFullYear() - 3
      ],
      is_published: false,
      should_publish_after_processing: true,
      temporary_upload_file: null,
      cover_temporary_upload_file: null,
      files: [],
      order: 0
    }
  },
  computed: {
    formDisabled () {
      return !this.cover_temporary_upload_file || !this.temporary_upload_file
    },
    actions () {
      return [
        {
          label: 'Salvar',
          action: this.newTextbook,
          icon: 'mdi-floppy',
          disabled: this.formDisabled
        }
      ]
    },
    displayCoverButton () {
      if (!this.cover_temporary_upload_file) {
        return true
      }
      return false
    },
    displayMaterialButton () {
      if (!this.temporary_upload_file) {
        return true
      }
      return false
    },
    enableName () {
      return this.type !== materialTypes.Teoric
    },
    enableGrade () {
      return ![materialTypes.Project, materialTypes.Supplementary].includes(this.type)
    },
    enableVolume () {
      return ![materialTypes.Project, materialTypes.Supplementary, materialTypes.Additional].includes(this.type)
    },
    enableSubject () {
      return [materialTypes.Teoric].includes(this.type)
    }
  },
  watch: {
    type (newVal) {
      if ([materialTypes.Additional].includes(newVal)) {
        this.$refs.gVolumeSelect.grade_volume_id = null
        this.$refs.gSubjectSelect.grade_subject_id = null
      }
      if ([materialTypes.Activity].includes(newVal)) {
        this.$refs.gSubjectSelect.grade_subject_id = null
        this.name = ''
      }
      if ([materialTypes.Project, materialTypes.Supplementary].includes(newVal)) {
        this.$refs.gSelect.grade_id = null
      }
    }
  },
  created () {
    this.textbookCreate = utils.checkPermissions('subject-textbooks.create')
    this.fetchAll()
  },
  methods: {
    listTypes,
    listVolumeTypes,
    fetchAll () {
      this.types = listTypes()
    },

    async confirmNewTextbook () {
      this.$dialog.confirm('Deseja cadastrar um novo material ?')
        .cancelLabel('não')
        .label('Sim')
        .onConfirm(() => {
          this.resetForm()
        })
        .onCancel(() => {
          this.$router.go(-1)
        })
        .show()
    },
    openUploadCoverDialog () {
      this.$dialog.custom(UploadImageDialog).with({ title: 'da capa' }).show()
        .then(async (formData) => {
          if (!formData) {
            return
          }
          try {
            this.loadingCover = true
            const idTempFile = await this.$api.uploadCover(formData)
            this.cover_temporary_upload_file = idTempFile.id
          } catch {
            this.$store.dispatch('setSnackBar', { text: 'Não foi possível salvar a capa, tente novamente mais tarde.', color: 'red' })
          } finally {
            this.loadingCover = false
          }
        })
    },

    openUploadMaterialDialog () {
      this.$dialog.custom(UploadFileDialog).with({ title: 'do material' }).show()
        .then(async (formData) => {
          if (!formData) {
            return
          }
          try {
            this.loadingMaterial = true
            const idTempFile = await this.$api.upload(formData)
            this.temporary_upload_file = idTempFile.id
          } catch {
            this.$store.dispatch('setSnackBar', { text: 'Não foi possível salvar o material, tente novamente mais tarde.', color: 'red' })
          } finally {
            this.loadingMaterial = false
          }
        })
    },

    resetForm () {
      this.name = ''
      this.temporary_upload_file = null
      this.cover_temporary_upload_file = null
      this.subject_id = null
      this.type = null
      this.grade_level_group = null
      this.grade_level_id = null
      this.order = 0
      this.formDisabled = true
      this.publishing_year = null
    },
    async newTextbook () {
      this.cleanFormErrors()
      this.loading = true
      const data = {
        cover_temporary_upload_file_id: this.cover_temporary_upload_file,
        temporary_upload_file_id: this.temporary_upload_file,
        type: this.type,
        publishing_year: this.publishing_year,
        education_system_id: this.education_system_id,
        education_stage_id: this.education_stage_id,
        grade_id: this.grade_id,
        grade_volume_id: this.grade_volume_id,
        grade_subject_id: this.grade_subject_id,
        name: this.name,
        order: this.order,
        is_teacher_material: this.is_teacher_material,
        should_publish_after_processing: this.should_publish_after_processing
      }
      try {
        await this.$api.newTextbook(data)
        this.$store.dispatch('setSnackBar', { text: 'Material cadastrado com sucesso.' })
        this.confirmNewTextbook()
      } catch (e) {
        this.$store.dispatch('setSnackBar', { text: 'Não foi possível cadastrar o material.', color: 'red' })
        this.getErrors(e)
      } finally {
        this.loading = false
      }
    }
  }
}
</script>
